/**
 * Report — the index body for the per-scan analysis route.
 *
 * Renders the Components / Vulnerabilities tables based on the
 * `?tab=` URL param. The surrounding chrome (PageLayout + scan
 * sub-header + tab strip) is owned by [AnalysisLayout](./AnalysisLayout.jsx),
 * which mounts as the parent layout route and renders this body
 * through an <Outlet />.
 *
 * Reads scan-level state (analysisData, isLatest, versions) from
 * `useOutletContext()` so it doesn't have to re-fetch the scan
 * details or re-read `location.state`. When a vuln row is clicked
 * the navigation stays within the parent layout — the sub-header
 * does NOT remount.
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useOutletContext } from 'react-router-dom';
import ComponentsTable from './ComponentsTable';
import VulnerabilitiesTable from './VulnerabilitiesTable';
import VulnerabilityManagementService from '../../services/VulnerabilityManagementService';
import reportStyles from './Report.module.css';

const VALID_TABS = new Set(['components', 'vulnerabilities']);
const DEFAULT_TAB = 'components';

const Report = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { analysisData, isLatest, versions } = useOutletContext() ?? {};

  const [components, setComponents] = useState([]);
  const [allVulnerabilities, setAllVulnerabilities] = useState([]);
  const [componentsLoading, setComponentsLoading] = useState(false);
  const [vulnerabilitiesLoading, setVulnerabilitiesLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const rawTabParam = searchParams.get('tab');
  const activeTab = rawTabParam && VALID_TABS.has(rawTabParam) ? rawTabParam : DEFAULT_TAB;

  // Components — server-side pagination. Refetches on page / size
  // change. Returns early when uuid isn't ready or analysisData is
  // still loading in the parent layout.
  useEffect(() => {
    if (!uuid || !analysisData) return;
    const fetchComponents = async () => {
      try {
        setComponentsLoading(true);
        const data = await VulnerabilityManagementService.getAnalysisComponents(
          uuid,
          currentPage,
          pageSize,
        );
        setComponents(data.components || []);
      } catch (err) {
        console.error('Error fetching components:', err);
      } finally {
        setComponentsLoading(false);
      }
    };
    fetchComponents();
  }, [uuid, currentPage, pageSize, analysisData]);

  // Vulnerabilities — fetched once; frontend handles pagination
  // + filtering internally in VulnerabilitiesTable.
  useEffect(() => {
    if (!uuid) return;
    const fetchVulnerabilities = async () => {
      try {
        setVulnerabilitiesLoading(true);
        const data = await VulnerabilityManagementService.getAnalysisVulnerabilities(uuid);
        setAllVulnerabilities(data.vulnerabilities || []);
      } catch (err) {
        console.error('Error fetching vulnerabilities:', err);
      } finally {
        setVulnerabilitiesLoading(false);
      }
    };
    fetchVulnerabilities();
  }, [uuid]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Changing page size resets to page 1 — matches the dashboard
  // `handlePageSizeChange` pattern (71b92290f).
  const handlePageSizeChange = (next) => {
    setPageSize(next);
    setCurrentPage(1);
  };

  // Clicking a vuln row transitions to the detail body. The parent
  // AnalysisLayout stays mounted (same route prefix), so the
  // sub-header doesn't remount during the transition. Nav state
  // forwards isLatest + versions in case the user hard-refreshes
  // the detail URL — the layout's shellState caches them on mount.
  const handleVulnerabilityClick = (source, vulnId) => {
    navigate(
      `/athena/vulnerability-management/analysis/${uuid}/vulnerability/${vulnId}`,
      { state: { source, isLatest, versions } },
    );
  };

  return (
    <section className={reportStyles.tableSection}>
      {activeTab === 'components' ? (
        <ComponentsTable
          data={components}
          totalComponents={analysisData?.components || 0}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          loading={componentsLoading}
        />
      ) : (
        <VulnerabilitiesTable
          data={allVulnerabilities}
          loading={vulnerabilitiesLoading}
          onRowClick={handleVulnerabilityClick}
        />
      )}
    </section>
  );
};

export default Report;
